/** AI notification settings API client — wraps the tenant-scoped
 *  `/ai-security/notification-settings` endpoints on cnapp-api. Every route
 *  returns the full settings envelope, so this uses `apiFetch`, never `apiFetchVoid`. */

import type {
  NotificationCategoryId,
  NotificationThreshold,
} from '../../utils/prefs'

import { apiFetch } from './core'

// ---- Backend payload shapes (snake_case) -----------------------------------

/** One persisted row (D3 as read / D4 as sent). */
export type ApiNotificationSetting = {
  category: NotificationCategoryId
  enabled: boolean
  severity_threshold: NotificationThreshold | null
  /** RFC3339; `"0001-01-01T00:00:00Z"` for synthesized defaults. */
  effective_at: string
}

/** The write shape (D4): the PUT body carries no `effective_at` — the store
 *  assigns timestamps server-side, so any client-sent value is ignored. */
export type ApiNotificationSettingUpdate = {
  category: NotificationCategoryId
  enabled: boolean
  severity_threshold: NotificationThreshold | null
}

export type ApiNotificationSettingsResponse = {
  settings: ApiNotificationSetting[]
  /** Always an array, never null/undefined — guarded below so every consumer
   *  can iterate it without a nil check. */
  readable_categories: NotificationCategoryId[]
  defaults: Record<
    NotificationCategoryId,
    { enabled: boolean; severity_threshold: NotificationThreshold | null }
  >
}

const SETTINGS_PATH = '/ai-security/notification-settings'
const RESET_PATH = `${SETTINGS_PATH}/reset`

/** Guard against a nil slice marshaled as JSON null (Go footgun): the backend
 *  sends `[]`, but a null `readable_categories` would crash every consumer. */
const normalize = (
  res: ApiNotificationSettingsResponse,
): ApiNotificationSettingsResponse => ({
  settings: res.settings ?? [],
  readable_categories: res.readable_categories ?? [],
  defaults: res.defaults ?? ({} as ApiNotificationSettingsResponse['defaults']),
})

export const getNotificationSettings = async (
  tenant: string,
): Promise<ApiNotificationSettingsResponse> => {
  const res = await apiFetch<ApiNotificationSettingsResponse>(
    SETTINGS_PATH,
    {},
    tenant,
  )
  return normalize(res)
}

export const saveNotificationSettings = async (
  tenant: string,
  settings: ApiNotificationSettingUpdate[],
): Promise<ApiNotificationSettingsResponse> => {
  const res = await apiFetch<ApiNotificationSettingsResponse>(
    SETTINGS_PATH,
    { method: 'PUT', body: JSON.stringify({ settings }) },
    tenant,
  )
  return normalize(res)
}

export const resetNotificationSettings = async (
  tenant: string,
): Promise<ApiNotificationSettingsResponse> => {
  const res = await apiFetch<ApiNotificationSettingsResponse>(
    RESET_PATH,
    { method: 'POST' },
    tenant,
  )
  return normalize(res)
}
